import React from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { ReactComponent as CapsuleIcon } from '../../../images/icons/capsule.svg';
import { ReactComponent as CapsuleFillIcon } from '../../../images/icons/capsule_fill.svg';
import { CustomLegend } from './CustomLegend';
import { Box, Paper, Typography } from '@mui/material';
import {
  black,
  customError,
  dimGrey,
  lightBackground1,
  lightBlack,
  lightGrey,
  lightPurple,
  warningYellow,
} from '../../../mainTheme';

interface AdherenceCardProps {
  isSelected?: boolean;
  onClick?: () => void;
  data: {
    adherent: number;
    nonAdherent: number;
    unknown: number;
  };
}

const ADHERENCE_CONFIG = {
  categories: [
    { name: 'non-adherent', key: 'nonAdherent', color: customError },
    { name: 'unknown', key: 'unknown', color: warningYellow },
    { name: 'adherent', key: 'adherent', color: '#41AF60' },
  ],
  noData: {
    color: lightGrey,
    value: 1, // 1% to show the pie chart
  },
} as const;

export const AdherenceCard: React.FC<AdherenceCardProps> = ({
  isSelected,
  onClick,
  data,
}) => {
  const total = data.adherent + data.nonAdherent + data.unknown;
  const isNoData = total === 0;

  // Step 1: Compute initial percentages
  let percentages = {
    adherent: (data.adherent / total) * 100,
    nonAdherent: (data.nonAdherent / total) * 100,
    unknown: (data.unknown / total) * 100,
  };

  // Step 2: Round the values
  let roundedPercentages = {
    adherent: Math.round(percentages.adherent),
    nonAdherent: Math.round(percentages.nonAdherent),
    unknown: Math.round(percentages.unknown),
  };

  // Step 3: Adjust the sum to ensure it equals 100%
  let sum =
    roundedPercentages.adherent +
    roundedPercentages.nonAdherent +
    roundedPercentages.unknown;
  let diff = 100 - sum;

  // If there's a difference, adjust the largest category
  if (diff !== 0) {
    let maxKey = Object.keys(roundedPercentages).reduce((a, b) =>
      roundedPercentages[a as keyof typeof roundedPercentages] >
      roundedPercentages[b as keyof typeof roundedPercentages]
        ? a
        : b,
    ) as keyof typeof roundedPercentages;
    roundedPercentages[maxKey] += diff; // Adjust to fix rounding error
  }

  const chartData = ADHERENCE_CONFIG.categories.map((category) => ({
    name: category.name,
    value: roundedPercentages[category.key as keyof typeof roundedPercentages],
    color: category.color,
  }));

  if (isNoData) {
    chartData[2].value = ADHERENCE_CONFIG.noData.value; // add dummy data to show the pie chart
  }

  return (
    <>
      <Paper
        elevation={isSelected ? 8 : 1}
        sx={{
          px: '24px',
          py: '21px',
          cursor: 'pointer',
          transition: 'all 0.3s',
          transform: isSelected ? 'scale(1.02)' : 'none',
          '&:hover': {
            elevation: 3,
          },
          borderRadius: '8px',
          border: isSelected
            ? `1px solid ${lightPurple}`
            : '1px solid var(--Outline-O5---Separator, #D4D4D9)',
          boxShadow: isSelected
            ? '-4px 8px 24px 0px rgba(0, 0, 0, 0.25)'
            : 'none',
          height: '152px',
          flex: '1 0 0',
          overflow: 'hidden',
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
          }}
        >
          {/* Left section: title+icon, then legend */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
            justifyContent="space-between"
            alignItems="space-between"
          >
            <Box sx={{ display: 'flex' }}>
              <Typography
                sx={{
                  color: isSelected ? lightBlack : dimGrey,
                  fontSize: '16px',
                  fontWeight: 700,
                  letterSpacing: '0.2px',
                  mr: 1,
                }}
              >
                Adherence
              </Typography>
              {isSelected ? <CapsuleFillIcon /> : <CapsuleIcon />}
            </Box>

            <CustomLegend
              payload={chartData.map((entry) => ({
                name: entry.name,
                color: entry.color,
                value: isNoData ? 'N/A' : `${entry.value}%`,
              }))}
            />
          </Box>
          {/* Right section: pie chart only */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PieChart width={112} height={112} style={{ flexShrink: 0 }}>
              <circle
                cx="56"
                cy="56"
                r="25"
                fill={isNoData ? ADHERENCE_CONFIG.noData.color : '#B5E3C2'}
              />
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={55}
                dataKey="value"
                startAngle={15}
                endAngle={374.9}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      isNoData ? ADHERENCE_CONFIG.noData.color : entry.color
                    }
                  />
                ))}
              </Pie>
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={16}
                fill={isNoData ? lightBackground1 : black} // White text if no data
              >
                {isNoData ? 'N/A' : `${roundedPercentages.adherent}%`}
              </text>
            </PieChart>
          </Box>
        </Box>
      </Paper>
    </>
  );
};
